import { absoluteUrl, DEFAULT_DESCRIPTION, SITE_NAME } from './seo';

/**
 * @typedef {{
 *   path?: string;
 *   url?: string;
 *   title?: string;
 *   text?: string;
 *   scopeLabel?: string;
 *   location?: string;
 * }} SharePayloadOverride
 */

/** @type {SharePayloadOverride} */
export const HOME_PAGE_SHARE = {
  path: '/',
  title: `Compare products from online shops in Malta | ${SITE_NAME}`,
  text: DEFAULT_DESCRIPTION,
};

/**
 * @param {{ pathname: string, search: string }} location
 * @param {SharePayloadOverride | null | undefined} override
 */
export function resolveSharePayload(location, override) {
  if (override?.url) {
    return {
      url: override.url,
      title: override.title ?? SITE_NAME,
      text: override.text ?? DEFAULT_DESCRIPTION,
    };
  }

  const path =
    override?.path != null && override.path !== ''
      ? override.path
      : `${location.pathname}${location.search}`;
  const url = absoluteUrl(path);

  if (override?.scopeLabel && override.scopeLabel.trim() !== '') {
    const scope = override.scopeLabel.trim();
    return {
      url,
      title: override.title ?? `Compare ${scope} | ${SITE_NAME}`,
      text:
        override.text
        ?? `Compare ${scope} from Maltese online stores — prices and stock on ${SITE_NAME}.`,
    };
  }

  return {
    url,
    title: override?.title ?? `${SITE_NAME} — compare Malta shops`,
    text: override?.text ?? DEFAULT_DESCRIPTION,
  };
}

const MOBILE_SHARE_MQ = '(max-width: 768px)';

/**
 * Matches the site header breakpoint: mobile nav uses the more menu.
 *
 * @returns {boolean}
 */
export function isMobileShareViewport() {
  return (
    typeof window !== 'undefined'
    && window.matchMedia(MOBILE_SHARE_MQ).matches
  );
}

/**
 * @returns {boolean}
 */
export function canUseNativeShare() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * @param {{ url: string, title?: string, text?: string }} payload
 */
export async function nativeShare(payload) {
  if (!canUseNativeShare()) {
    return 'unsupported';
  }

  try {
    await navigator.share({
      url: payload.url,
    });
    return 'native';
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      return 'cancelled';
    }
    return 'error';
  }
}

/**
 * @param {string} url
 */
export async function copyShareUrl(url) {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return true;
  }

  const input = document.createElement('input');
  input.value = url;
  input.setAttribute('readonly', '');
  input.style.position = 'fixed';
  input.style.left = '-9999px';
  document.body.appendChild(input);
  input.select();
  const ok = document.execCommand('copy');
  document.body.removeChild(input);
  return ok;
}
